<template>
  <div>
    <AppCard v-if="error || pending || rows.length"
             elevation="0"
             :data-locator="dataLocator"
    >
      <app-alert-in-page v-if="error"
                         :dismissible="false"
                         variant="error"
                         no-icon
      >
        {{ errorText }}
      </app-alert-in-page>

      <div v-else-if="pending" :data-locator="`${dataLocator}-loading`">
        <v-skeleton-loader type="table"/>
      </div>

      <template v-else>
        <p class="mb-5 text--medium font-weight-bold text-text-primary">
          {{ title }}
        </p>

        <div class="SubscriptionConsumptionHistoryTable__wrapper">
          <v-data-table
            :headers="headers"
            :items="paginatedRows"
            :row-props="computedRowProps"
            :items-per-page="-1"
            hide-default-footer
            class="SubscriptionConsumptionHistoryTable__table"
            :data-locator="`${dataLocator}-table`"
          />
        </div>

        <div class="d-flex justify-end mt-4">
          <AppPagination v-model:current-page="currentPage"
                         v-model:page-size="itemsPerPage"
                         :page-sizes="ITEMS_PER_PAGE"
                         :total="rows.length"
          />
        </div>
      </template>
    </AppCard>

    <SubscriptionConsumptionEmptyPanel
      v-else
      icon="insert_chart"
      :title="emptyTitle"
      :data-locator="`${dataLocator}-empty`"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from 'vue'
  import { useStore } from 'vuex'

  import AppCard from '@/common/AppCard.vue'
  import AppPagination from '@/common/pagination/AppPagination.vue'
  import { usePagination } from '@/common/pagination/usePagination'
  import SubscriptionConsumptionEmptyPanel from '@/subscriptions/views/SubscriptionConsumptionEmptyPanel.vue'

  const ITEMS_PER_PAGE = [10, 25, 50]

  type HistoryRow = { id: number, [key: string]: unknown }
  type HistoryTableHeader = { title: string, key: string, sortable?: boolean }

  // Shared history-table scaffold (error / loading / table / pagination / empty). Parents own
  // the data and pass already-sorted `rows`; forfait and pay-per-use tabs both use it.
  export default defineComponent({
    name: 'SubscriptionConsumptionHistoryTable',
    components: { AppCard, AppPagination, SubscriptionConsumptionEmptyPanel },
    props: {
      title: { type: String, required: true },
      headers: { type: Array as PropType<HistoryTableHeader[]>, required: true },
      rows: { type: Array as PropType<HistoryRow[]>, required: true },
      pending: { type: Boolean, default: false },
      error: { type: Boolean, default: false },
      errorText: { type: String, default: '' },
      emptyTitle: { type: String, required: true },
      dataLocator: { type: String, required: true },
      rowLocator: { type: String, required: true },
      paginationKey: { type: String, required: true },
      search: { type: String, default: '' },
    },
    setup (props) {
      const store = useStore()
      const { currentPage, itemsPerPage } = usePagination({
        currentUserId: store.getters['user/currentUserId'],
        userSettingsKey: props.paginationKey,
        pageSize: ITEMS_PER_PAGE[0],
      })

      return { currentPage, itemsPerPage }
    },
    data () {
      return {
        ITEMS_PER_PAGE,
      }
    },
    computed: {
      paginatedRows (): HistoryRow[] {
        const start = (this.currentPage - 1) * this.itemsPerPage
        return this.rows.slice(start, start + this.itemsPerPage)
      },
    },
    watch: {
      // Back to page 1 whenever the search changes, even when it yields the same row count.
      search () {
        this.currentPage = 1
      },
    },
    methods: {
      computedRowProps ({ item }: { item: HistoryRow }) {
        return { 'data-locator': this.rowLocator, 'data-id': item.id }
      },
    },
  })
</script>

<style scoped lang="scss">
  .SubscriptionConsumptionHistoryTable__wrapper {
    border: 1px solid rgb(var(--v-theme-border-primary));
    border-radius: 8px;
    overflow: hidden;
  }

  .SubscriptionConsumptionHistoryTable__table {
    :deep(th),
    :deep(td) {
      padding: 16px !important;
      font-size: 14px !important;
      line-height: 20px;
    }
  }
</style>
